import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Chip, Collapse, Divider, Grid, IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, SelectChangeEvent, CircularProgress, Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText, Tooltip, TextField, Snackbar, FormControl, InputLabel, OutlinedInput, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import EventBusyIcon from '@mui/icons-material/EventBusy';
// import BuildCircleIcon from '@mui/icons-material/BuildCircle'; // icona guasto tecnico, commentata con il relativo indicatore
import FlagIcon from '@mui/icons-material/Flag';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Call, CallSearch } from '../types/calls';
import { callService } from '../services/callService';
import { useAuth } from '../contexts/AuthContext';

const maskPhone = (number?: string): string => {
  if (!number) return '-';
  const cleaned = number.replace(/\s+/g, '');
  const suffix = cleaned.slice(-3);
  return `+39 •••••••${suffix}`;
};

// --- COMPONENTE VISUALIZZATORE CONTENUTO FONTE (SEMPLIFICATO) ---
const SourceContent: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return <Typography color="text.secondary">Nessun dato disponibile.</Typography>;

  // Recupera il testo da qualsiasi campo disponibile
  const contentText = data.text || data.content || data.pageContent || '';

  return (
    <Box>
       {/* Mostra il testo del chunk in un box scrollabile */}
       {contentText ? (
        <>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1, color: 'text.primary' }}>Contenuto Fonte:</Typography>
            <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: '#fff', maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {contentText}
                </Typography>
            </Paper>
        </>
       ) : (
         <Typography color="text.secondary" variant="body2">Nessun contenuto testuale disponibile.</Typography>
       )}
    </Box>
  );
};

interface TranscriptViewerProps {
  transcript: string;
  searches: CallSearch[];
  loading: boolean;
  onShowSearch: (data: CallSearch) => void;
}

const TranscriptViewer: React.FC<TranscriptViewerProps> = ({ transcript, searches, loading, onShowSearch }) => {
  const transcriptLines = transcript.split('\n').filter(line => line.trim() !== '');
  
  const buttonDataMap = React.useMemo(() => {
    const map = new Map<number, CallSearch>();
    if (searches.length === 0) return map;
    
    // Normalizza le domande utente per il matching
    const userQuestionsWithSearch = new Map(searches.map(s => [s.userQuestion.trim().toLowerCase(), s]));
    
    transcriptLines.forEach((line, index) => {
      const parts = line.split(': ');
      if (parts.length < 2) return;
      
      const speaker = parts[0].toLowerCase();
      // Euristica: controlla se chi parla è l'utente
      if (speaker === 'user' || speaker === 'cliente' || speaker === 'caller') {
         const text = parts.slice(1).join(': ').trim().toLowerCase();
         if (userQuestionsWithSearch.has(text)) {
             const searchData = userQuestionsWithSearch.get(text);
             // Cerca la prossima risposta AI per attaccare il bottone lì vicino
             for (let i = index + 1; i < transcriptLines.length; i++) {
                const nextParts = transcriptLines[i].split(': ');
                const nextSpeaker = nextParts[0].toLowerCase();
                if (nextSpeaker === 'ai' || nextSpeaker === 'bot' || nextSpeaker === 'assistente') {
                    map.set(i, searchData!);
                    break;
                }
             }
         }
      }
    });
    return map;
  }, [transcriptLines, searches]);

  if (loading) {
    return (<Box display="flex" justifyContent="center" alignItems="center" height={100}><CircularProgress size={30} /><Typography sx={{ ml: 2 }}>Analisi trascrizione in corso...</Typography></Box>);
  }

  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {transcriptLines.map((line, index) => {
        const parts = line.split(': ');
        const speaker = parts[0].toLowerCase();
        const isAI = speaker === 'ai' || speaker === 'bot' || speaker === 'assistente';
        const text = parts.slice(1).join(': ').trim();
        const searchDataForThisButton = buttonDataMap.get(index);
        
        return (
          <Box key={index} sx={{ display: 'flex', justifyContent: isAI ? 'flex-start' : 'flex-end' }}>
            <Box sx={{ maxWidth: '85%' }}>
              <Paper elevation={1} sx={{ p: '8px 14px', borderRadius: isAI ? '20px 20px 20px 5px' : '20px 20px 5px 20px', bgcolor: isAI ? 'primary.main' : '#E0E0E0', color: isAI ? 'primary.contrastText' : 'black', border: '1px solid', borderColor: isAI ? 'primary.dark' : '#bdbdbd' }}>
                <Typography variant="body2">{text}</Typography>
              </Paper>
              {searchDataForThisButton && (
                <Button size="small" variant="text" startIcon={<SearchIcon sx={{ fontSize: '1rem' }} />} onClick={() => onShowSearch(searchDataForThisButton)} sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.7rem', color: 'primary.main', float: 'left' }}>
                  Vedi Ricerca AI
                </Button>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// L'URL firmato si chiede solo al click: ogni richiesta è un accesso registrato
// nell'audit, quindi espandere una riga non deve contare come ascolto.
const RecordingPlayer: React.FC<{ callId: string }> = ({ callId }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setUrl(await callService.getRecordingUrl(callId));
    } catch {
      setError('Registrazione non disponibile.');
    } finally {
      setLoading(false);
    }
  };

  if (url) {
    // Un URL scaduto fallisce al play: si torna al pulsante per chiederne uno nuovo.
    return <Box component="audio" controls autoPlay src={url} onError={() => setUrl(null)} sx={{ width: '100%', mt: 1 }} />;
  }
  return (
    <Box sx={{ mt: 1 }}>
      <Button variant="outlined" size="small" onClick={load} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : undefined}>
        Ascolta registrazione
      </Button>
      {error && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>}
    </Box>
  );
};

const ChiamatePage: React.FC = () => {
  const { user } = useAuth();
  const [chiamate, setChiamate] = useState<Call[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [filtroSoddisfazione, setFiltroSoddisfazione] = useState<'tutti' | 'buoni' | 'medi' | 'bassi'>('tutti');
  // Estremi temporali: la data porta il filtro, l'ora è facoltativa e affina.
  // Ora vuota → il backend allarga alla giornata intera.
  const [daData, setDaData] = useState('');
  const [daOra, setDaOra] = useState('');
  const [aData, setAData] = useState('');
  const [aOra, setAOra] = useState('');
  // Due stati distinti: quello digitato e quello effettivamente interrogato,
  // così il debounce non rallenta la scrittura nel campo.
  const [callIdInput, setCallIdInput] = useState('');
  const [callIdFiltro, setCallIdFiltro] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneFiltro, setPhoneFiltro] = useState('');
  const [motivoInput, setMotivoInput] = useState('');
  const [motivoFiltro, setMotivoFiltro] = useState('');
  // '' = tutte; '0' comprende anche il dato mancante, che la tabella mostra come "No".
  const [filtroTrasferita, setFiltroTrasferita] = useState<'' | '0' | '1'>('');
  const [filtroPrenotazione, setFiltroPrenotazione] = useState<'' | '0' | '1'>('');
  const [dettagliAperti, setDettagliAperti] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  
  // Stati Modale Ricerca AI
  const [modalData, setModalData] = useState<CallSearch | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchesLoading, setSearchesLoading] = useState(false);
  const [searches, setSearches] = useState<CallSearch[]>([]);

  // Stati Modale Report
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportCallId, setReportCallId] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [reportCategory, setReportCategory] = useState('');
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportSuccessMessage, setReportSuccessMessage] = useState<string | null>(null);

  const handleShowSearchModal = (data: CallSearch) => { setModalData(data); setModalOpen(true); };
  const handleCloseModal = () => setModalOpen(false);

  // --- Handlers Segnalazione ---
  const handleOpenReportDialog = (callId: string) => {
    setReportCallId(callId);
    setReportReason('');
    setReportCategory('');
    setReportDialogOpen(true);
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setReportCallId(null);
  };

  const handleSubmitReport = async () => {
    if (!reportCallId || !reportReason.trim() || !reportCategory) return;

    setIsSendingReport(true);
    try {
      await callService.reportCall(reportCallId, reportReason, reportCategory);
      setReportSuccessMessage(`Segnalazione inviata con successo.`);
      handleCloseReportDialog();
    } catch (err) {
      console.error(err);
      setErrore(err instanceof Error ? err.message : 'Errore durante l\'invio della segnalazione.');
    } finally {
      setIsSendingReport(false);
    }
  };

  // I campi di testo si interrogano dopo la pausa di digitazione, non a ogni carattere.
  useEffect(() => {
    const t = setTimeout(() => {
      setCallIdFiltro(callIdInput); setPhoneFiltro(phoneInput); setMotivoFiltro(motivoInput); setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [callIdInput, phoneInput, motivoInput]);

  const fetchData = useCallback(async () => {
    setLoading(true); setErrore(null);
    try {
      const res = await callService.getCalls({
        page: page + 1,
        perPage: rowsPerPage,
        satisfactionFilter: filtroSoddisfazione,
        daData, daOra, aData, aOra,
        callId: callIdFiltro,
        phone: phoneFiltro,
        motivo: motivoFiltro,
        trasferita: filtroTrasferita,
        vuolePrenotare: filtroPrenotazione,
      });
      setChiamate(res.calls); setTotal(res.total);
    } catch (error) {
      setErrore(error instanceof Error ? error.message : 'Si è verificato un errore sconosciuto.');
    } finally { setLoading(false); }
  }, [page, rowsPerPage, filtroSoddisfazione, daData, daOra, aData, aOra, callIdFiltro,
      phoneFiltro, motivoFiltro, filtroTrasferita, filtroPrenotazione]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleDettagli = async (callId: string | undefined, dbId: number) => {
    if (dettagliAperti === dbId) {
      setDettagliAperti(null); setSearches([]);
    } else {
      setDettagliAperti(dbId); setSearches([]);
      if (!callId) { setErrore("ID chiamata non disponibile per recuperare le ricerche IA."); return; }
      try {
        setSearchesLoading(true);
        const searchResults = await callService.getCallSearches(callId);
        setSearches(searchResults);
      } catch (err) {
        setErrore(err instanceof Error ? err.message : 'Impossibile caricare le ricerche IA.');
      } finally { setSearchesLoading(false); }
    }
  };

  const handleFilterChange = (event: SelectChangeEvent<'tutti' | 'buoni' | 'medi' | 'bassi'>) => {
    setFiltroSoddisfazione(event.target.value as 'tutti' | 'buoni' | 'medi' | 'bassi');
    setPage(0);
  };
  const filtriAttivi = filtroSoddisfazione !== 'tutti' || !!(daData || daOra || aData || aOra
    || callIdInput || phoneInput || motivoInput || filtroTrasferita || filtroPrenotazione);
  const azzeraFiltri = () => {
    setFiltroSoddisfazione('tutti');
    setDaData(''); setDaOra(''); setAData(''); setAOra('');
    setCallIdInput(''); setPhoneInput(''); setMotivoInput('');
    setFiltroTrasferita(''); setFiltroPrenotazione('');
    setPage(0);
  };
  const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Chiamate Ricevute</Typography>
      {/* Barra filtri. Gli orari sono facoltativi: lasciandoli vuoti l'estremo
          copre l'intera giornata, così "le chiamate del 21" costa due click.
          Nessuna etichetta di offset accanto ai campi — l'ora è quella italiana
          e un "GMT+2" fisso sarebbe falso da fine ottobre. */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Cerca: i campi con cui si ritrova una chiamata precisa. */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" InputLabelProps={{ shrink: true }} label="Telefono" placeholder="anche solo le ultime cifre"
              value={phoneInput} onChange={e => setPhoneInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              fullWidth size="small" InputLabelProps={{ shrink: true }} label="Motivo" placeholder="es. carta identità"
              value={motivoInput} onChange={e => setMotivoInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" InputLabelProps={{ shrink: true }} label="ID chiamata" placeholder="anche solo l'inizio"
              value={callIdInput} onChange={e => setCallIdInput(e.target.value)}
            />
          </Grid>

          {/* Periodo: ogni data ha accanto la sua ora facoltativa. */}
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth size="small" type="date" label="Da" InputLabelProps={{ shrink: true }}
              value={daData} onChange={e => { setDaData(e.target.value); setPage(0); }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth size="small" type="time" label="Ora (opz.)" InputLabelProps={{ shrink: true }}
              value={daOra} onChange={e => { setDaOra(e.target.value); setPage(0); }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth size="small" type="date" label="A" InputLabelProps={{ shrink: true }}
              value={aData} onChange={e => { setAData(e.target.value); setPage(0); }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth size="small" type="time" label="Ora (opz.)" InputLabelProps={{ shrink: true }}
              value={aOra} onChange={e => { setAOra(e.target.value); setPage(0); }}
            />
          </Grid>

          {/* Esito: gli attributi mostrati come chip in tabella. */}
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Soddisfazione</InputLabel>
              <Select label="Soddisfazione" value={filtroSoddisfazione} onChange={handleFilterChange}>
                <MenuItem value="tutti">Tutti</MenuItem>
                <MenuItem value="buoni">Buoni (8-10)</MenuItem>
                <MenuItem value="medi">Medi (5-7)</MenuItem>
                <MenuItem value="bassi">Bassi (0-4)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Trasferita</InputLabel>
              <Select displayEmpty input={<OutlinedInput notched label="Trasferita" />} value={filtroTrasferita}
                onChange={e => { setFiltroTrasferita(e.target.value as '' | '0' | '1'); setPage(0); }}>
                <MenuItem value="">Tutte</MenuItem>
                <MenuItem value="1">Sì</MenuItem>
                <MenuItem value="0">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Prenotazione</InputLabel>
              <Select displayEmpty input={<OutlinedInput notched label="Prenotazione" />} value={filtroPrenotazione}
                onChange={e => { setFiltroPrenotazione(e.target.value as '' | '0' | '1'); setPage(0); }}>
                <MenuItem value="">Tutte</MenuItem>
                <MenuItem value="1">Sì</MenuItem>
                <MenuItem value="0">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button startIcon={<RestartAltIcon />} onClick={azzeraFiltri} disabled={!filtriAttivi}>
              Azzera filtri
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {errore && (<Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrore(null)}>{errore}</Alert>)}
      
      <Snackbar 
        open={!!reportSuccessMessage} 
        autoHideDuration={6000} 
        onClose={() => setReportSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setReportSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {reportSuccessMessage}
        </Alert>
      </Snackbar>

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: '40px' }} />
                <TableCell><strong>Telefono</strong></TableCell>
                <TableCell><strong>Motivo</strong></TableCell>
                <TableCell><strong>Data/Ora</strong></TableCell>
                <TableCell><strong>Durata (s)</strong></TableCell>
                <TableCell><strong>Trasferita</strong></TableCell>
                <TableCell><strong>Prenotazione</strong></TableCell>
                <TableCell><strong>Obiettivo</strong></TableCell>
                <TableCell><strong>Costo Totale (€)</strong></TableCell>
                <TableCell><strong>Dettagli</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={10} align="center"><CircularProgress /></TableCell></TableRow>
               : chiamate.map((row) => {
                  const costoTotale = (row.cost_eur ?? 0) + (row.costo_telefonia_eur ?? 0);
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow hover>
                        <TableCell>
                          {row.is_critical && (
                            <Tooltip title="Rilevata possibile criticità (linguaggio, tono)">
                              <ReportProblemIcon color="error" />
                            </Tooltip>
                          )}
                          {/* Indicatore guasto tecnico Vapi: temporaneamente commentato, la
                              tabella torna esteticamente com'era prima della change. Il dato
                              continua ad arrivare (ended_reason, tool_errors, problema_tecnico)
                              e la mail di alert parte comunque: qui è sospesa solo la resa a
                              schermo, in attesa di chiudere il falso positivo sul risultato
                              mancante quando il cittadino riaggancia con un tool in volo.
                          {row.problema_tecnico && (
                            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{[row.ended_reason, row.tool_errors].filter(Boolean).join('\n') || 'Guasto tecnico'}</span>}>
                              <BuildCircleIcon color="warning" />
                            </Tooltip>
                          )}
                          */}
                        </TableCell>
                        <TableCell>{maskPhone(row.phone_number)}</TableCell>
                        <TableCell>{row.motivo_chiamata ?? '-'}</TableCell>
                        <TableCell>{row.start_time ? new Date(row.start_time).toLocaleString('it-IT') : '-'}</TableCell>
                        <TableCell>{row.duration_seconds ?? '-'}</TableCell>
                        <TableCell><Chip label={row.trasferita ? 'Sì' : 'No'} color={row.trasferita ? 'primary' : 'default'} size="small"/></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip label={row.vuole_prenotare ? 'Sì' : 'No'} color={row.vuole_prenotare ? 'primary' : 'default'} size="small"/>
                            {row.booking_error && (
                              <Tooltip title="Errore nella prenotazione (risposta 4xx/5xx dal sistema)">
                                <EventBusyIcon color="error" fontSize="small" />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell><Chip label={row.satisfaction_score ?? '-'} color={row.satisfaction_score == null ? 'default' : row.satisfaction_score >= 8 ? 'success' : row.satisfaction_score >= 6 ? 'default' : 'error'} sx={row.satisfaction_score != null && row.satisfaction_score >= 6 && row.satisfaction_score < 8 ? { bgcolor: '#c8e6c9', color: '#1b5e20' } : undefined} size="small" /></TableCell>
                        <TableCell>{costoTotale > 0 ? costoTotale.toFixed(2) : '-'}</TableCell>
                        <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="Vedi Dettagli e Trascrizione">
                                        <IconButton size="small" onClick={() => toggleDettagli(row.call_id, row.id)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                <Tooltip title="Segnala problema via Email">
                                    <IconButton size="small" color="error" onClick={() => handleOpenReportDialog(row.call_id || '')}>
                                        <FlagIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ padding: 0, borderBottom: 'none' }} colSpan={10}>
                          <Collapse in={dettagliAperti === row.id} timeout="auto" unmountOnExit>
                            <Box m={1.5} p={3} border={1} borderColor="divider" borderRadius={2} bgcolor="#fafafa">
                              <Grid container spacing={3}>
                                <Grid item xs={12} md={6}><Typography variant="h6" gutterBottom>👤 Dati Contatto</Typography><Typography><strong>Nome:</strong> {row.first_name ?? '-'} {row.last_name ?? ''}</Typography><Typography><strong>Telefono:</strong> {row.phone_number ?? '-'}</Typography><Typography><strong>Email:</strong> {row.email ?? '-'}</Typography><Divider sx={{ my: 2 }} /><Typography variant="subtitle2" color="text.secondary">ID Chiamata</Typography><Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{row.call_id}</Typography></Grid>
                                <Grid item xs={12} md={6}><Typography variant="h6" gutterBottom>🔊 Audio e Analisi</Typography><Typography variant="subtitle2" color="text.secondary">Registrazione</Typography>{row.audio_url && row.call_id ? (<RecordingPlayer callId={row.call_id} />) : (<Typography variant="body2" color="text.secondary">Non disponibile</Typography>)}<Divider sx={{ my: 2 }} /><Typography variant="subtitle2" color="text.secondary">Analisi Obiettivo</Typography><Typography><strong>Punteggio:</strong> {row.satisfaction_score ?? '-'}/10</Typography><Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', bgcolor: 'background.paper', p: 1, borderRadius: 1 }}>{row.analisi_soddisfazione || "Nessuna analisi testuale fornita."}</Typography></Grid>
                                {/* Blocco "Problemi tecnici" sopra la trascrizione: temporaneamente
                                    commentato insieme all'icona in tabella (vedi sopra), così il
                                    pannello dettagli torna identico a prima della change.
                                {row.problema_tecnico && (
                                  <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="h6" gutterBottom>⚠️ Problemi tecnici</Typography>
                                    {row.ended_reason && (
                                      <Typography variant="body2"><strong>Motivo di chiusura:</strong>{' '}
                                        <Box component="span" sx={{ fontFamily: 'monospace' }}>{row.ended_reason}</Box>
                                      </Typography>
                                    )}
                                    {row.tool_errors && (
                                      <>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Operazioni fallite</Typography>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontFamily: 'monospace', bgcolor: 'background.paper', p: 1, borderRadius: 1, wordBreak: 'break-word' }}>{row.tool_errors}</Typography>
                                      </>
                                    )}
                                  </Grid>
                                )}
                                */}
                                <Grid item xs={12}>
                                  <Divider sx={{ my: 2 }} />
                                  <Typography variant="h6" gutterBottom>💬 Trascrizione Completa</Typography>
                                  <TranscriptViewer transcript={row.transcript || "Trascrizione non disponibile."} searches={searches} loading={searchesLoading} onShowSearch={handleShowSearchModal} />
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              {!loading && chiamate.length === 0 && (<TableRow><TableCell colSpan={10} align="center">Nessuna chiamata trovata.</TableCell></TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} onPageChange={handlePageChange} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleRowsPerPageChange} rowsPerPageOptions={[5, 10, 25]} labelRowsPerPage="Righe per pagina:"/>
      </Paper>
      
      {/* --- DIALOGO RAG AGGIORNATO (NO METADATI VUOTI) --- */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoOutlinedIcon color="primary"/> Dettaglio Interazione RAG
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8f9fa', p: 3 }}>
          
          {/* 1. DOMANDA UTENTE */}
          <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon color="action" fontSize="small"/>
                <Typography variant="overline" color="text.secondary">Domanda Utente</Typography>
            </Box>
            <Typography sx={{ fontStyle: 'italic', pl: '28px' }}>"{modalData?.userQuestion}"</Typography>
          </Paper>

          {/* 2. RISPOSTA AI */}
          {modalData?.aiResponse && (
            <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#e3f2fd', borderColor: '#90caf9' }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ForumOutlinedIcon color="primary" fontSize="small"/>
                  <Typography variant="overline" color="primary">Risposta IA</Typography>
              </Box>
              <Typography sx={{ pl: '28px' }}>
                  {modalData.aiResponse}
              </Typography>
            </Paper>
          )}

          {/* 3. CITAZIONE */}
          {modalData?.citation && (
            <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fffde7', borderColor: '#f9a825', borderLeft: '4px solid #f9a825' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FormatQuoteIcon sx={{ color: '#f9a825' }} fontSize="small"/>
                <Typography variant="overline" sx={{ color: '#f57f17' }}>Citazione dalla base di conoscenza</Typography>
              </Box>
              <Typography variant="body2" sx={{ pl: '28px', fontStyle: 'italic', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                "{modalData.citation}"
              </Typography>
              {modalData.citationSummary && (
                <Typography variant="caption" sx={{ pl: '28px', display: 'block', mt: 1, color: 'text.secondary' }}>
                  {modalData.citationSummary}
                </Typography>
              )}
            </Paper>
          )}

          {/* 4. QUERY + FONTI */}
          <Paper elevation={0} variant="outlined" sx={{ p: 2.5, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <QueryStatsIcon color="action" fontSize="small"/>
              <Typography variant="overline" color="text.secondary">Ricerca effettuata</Typography>
            </Box>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: '#eee', p: 0.5, borderRadius: 1, display: 'block', mb: 2 }}>
              {modalData?.aiQuery}
            </Typography>

            {/* Nessuna fonte utile trovata */}
            {(modalData?.relevantChunks?.length ?? 0) === 0 && (modalData?.allChunks?.length ?? 0) > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                La base di conoscenza non conteneva informazioni utili per questa domanda. L'IA ha risposto in base al contesto della conversazione.
              </Typography>
            )}

            {/* Fonti rilevanti */}
            {(modalData?.relevantChunks?.length ?? 0) > 0 && (
              <Accordion disableGutters elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" fontWeight={600}>
                    Fonti usate dall'IA ({modalData?.relevantChunks?.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {modalData?.relevantChunks?.map((chunk, i) => (
                    <Box key={i} sx={{ p: 2, borderTop: i > 0 ? '1px solid #eee' : 'none' }}>
                      <Typography variant="caption" fontWeight={700} color="primary" display="block" mb={0.5}>
                        {chunk.metadata?.title || `Fonte ${i + 1}`}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'text.secondary', maxHeight: 200, overflowY: 'auto' }}>
                        {chunk.text || chunk.content}
                      </Typography>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Tutti i chunk recuperati */}
            {(modalData?.allChunks?.length ?? 0) > 0 && (
              <Accordion disableGutters elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" color="text.secondary">
                    Tutti gli estratti recuperati ({modalData?.totalChunks})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {modalData?.allChunks?.map((chunk, i) => (
                    <Box key={i} sx={{ p: 2, borderTop: i > 0 ? '1px solid #eee' : 'none', bgcolor: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.5}>
                        [{i}] {chunk.metadata?.title || `Estratto ${i + 1}`}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'text.primary', maxHeight: 250, overflowY: 'auto' }}>
                        {chunk.text || chunk.content}
                      </Typography>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: '12px 24px' }}>
          <Button onClick={handleCloseModal}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG SEGNALAZIONE PROBLEMA AGGIORNATO */}
      <Dialog open={reportDialogOpen} onClose={handleCloseReportDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagIcon color="error" /> Segnala Problema Chiamata
        </DialogTitle>
        <DialogContent>
            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                Stai inviando una segnalazione per la chiamata ID: <strong>{reportCallId}</strong>.<br/>
                Scegli l'area funzionale dove si è verificato l'errore per aiutare il team tecnico.
            </Typography>

            <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel id="category-label">Funzionalità su cui si è verificato l'errore</InputLabel>
                <Select
                    labelId="category-label"
                    value={reportCategory}
                    label="Funzionalità su cui si è verificato l'errore"
                    onChange={(e) => setReportCategory(e.target.value)}
                >
                    <MenuItem value="INFORMAZIONI">Risposte e Informazioni</MenuItem>
                    <MenuItem value="PRENOTAZIONI">Agenda e Appuntamenti</MenuItem>
                    <MenuItem value="ROUTING">Smistamento e Inoltro</MenuItem>
                    <MenuItem value="ALTRO">Altro / Errore Generico</MenuItem>
                </Select>
            </FormControl>

            <TextField
                margin="dense"
                id="reason"
                label="Descrivi il problema riscontrato"
                type="text"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Es: L'IA ha dato l'orario sbagliato..."
            />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseReportDialog} color="inherit">Annulla</Button>
            <Button 
                onClick={handleSubmitReport} 
                variant="contained" 
                color="error" 
                disabled={!reportReason.trim() || !reportCategory || isSendingReport}
                startIcon={isSendingReport ? <CircularProgress size={20} color="inherit"/> : null}
            >
                {isSendingReport ? 'Invio...' : 'Invia Segnalazione'}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChiamatePage;
